// public/v3/src/pages/scheduling.jsx — the provider "Scheduling" tab.
//
// Where a practice sets what patient self-scheduling offers: the calendar
// physician, weekly hours, visit types, date overrides and booking rules. The
// patient picks from this on the last step of the intake link (PR3); Zephyr
// reschedules against it (PR5). The on/off switch itself is the admin toggle in
// Settings ("Patient scheduling") — this page shows the state and links there.
//
// Server: routes/scheduling.js, requireRole('admin','provider') +
// requirePermission('scheduling'). No dormancy 404: the page works before the
// flag is on, so a practice can set up first and switch on second.
//
// Save model: EXPLICIT Save with the server's optimistic `version`. Every edit
// lands in a draft; dirty = fingerprint(draft) ≠ fingerprint(saved). Save is
// offered only while the client-side mirror of the server's validation passes
// (lib/scheduling-config-model.js). A 409 means someone else saved first: their
// version is reloaded and the person is told. Because a tab switch unmounts
// this page (main.jsx renders one page at a time), an unsaved draft is parked
// in a module-level variable and restored on the next mount when the server's
// version is still the one it was made from. Edits made while a Save is in
// flight are kept: the saved document becomes the baseline and the bar stays
// until they are saved too.
//
// Every time a person reads here is in the practice timezone (`timezone` from
// GET /config), and the preview says so.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ccmApi } from '../lib/api.js';
import { confirmDialog } from '../components/confirm-dialog.jsx';
import { CalendarSyncCard } from '../components/calendar-sync-card.jsx';
import {
  DAYS, WEEKDAYS, DAY_LABEL, LIMITS,
  hhmmToMin, formatHhmm12, timeOptions, validateRanges,
  setDayClosed, copyToWeekdays, addRange,
  newVisitType, validateVisitType, upsertVisitType, removeVisitType,
  validateOverride, upsertOverride, removeOverride, splitPastOverrides,
  normalizePhysicianId, normalizeConfig, validateConfig, configFingerprint, reconcileAfterSave, setupChecklist, previewBars,
  MIN_NOTICE_OPTIONS, MAX_ADVANCE_OPTIONS, GRANULARITY_OPTIONS, BUFFER_OPTIONS, HOLD_OPTIONS, withCurrentOption,
} from '../lib/scheduling-config-model.js';
import { todayYmd, tzLabel, formatDayMedium, dayParts, parseYmd, addDays, compareYmd, weekWindow, modeLabel } from '../lib/schedule-picker-model.js';
import '../styles/scheduling.css';

// The unsaved draft survives a tab switch here (see the header). Cleared on
// save, on discard, and whenever the server's version has moved on.
let retainedDraft = null; // { version, draft }

const PREVIEW_DEBOUNCE_MS = 500;
const DEFAULT_TZ = 'America/New_York';

/* ── small controls ────────────────────────────────────────────────────── */

// The 40×22 switch from Settings, inside a 44px label so a finger finds it.
function Toggle({ checked, onChange, disabled, label, text }) {
  return (
    <label className={`sched-toggle-wrap${disabled ? ' is-disabled' : ''}`}>
      <span className="settings-toggle">
        <input type="checkbox" checked={!!checked} disabled={!!disabled} aria-label={label}
          onChange={(e) => onChange(e.target.checked)} />
        <span className="settings-toggle-slider" />
      </span>
      {text ? <span className="sched-toggle-text">{text}</span> : null}
    </label>
  );
}

function Segmented({ value, options, onChange, disabled, label }) {
  return (
    <div className="sched-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" className="sched-seg-btn" aria-pressed={o.value === value}
          disabled={!!disabled} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

// Time entry is a <select> of 15-minute steps (6:00 AM–9:00 PM): the same on
// desktop and iPad, typeable, always on the grid — iPad Safari ignores `step`
// on type=time and desktop Safari has no time picker. A saved off-grid value
// is kept as an option so the control never silently changes it. The end
// select lists only times after the start (plus its own value, so an inverted
// range stays visible next to its error).
function TimeSelect({ value, onChange, disabled, label, after }) {
  const options = useMemo(() => {
    const all = timeOptions({ include: value ? [value] : [] });
    if (after == null || hhmmToMin(after) == null) return all;
    return all.filter((o) => o.value === value || hhmmToMin(o.value) > hhmmToMin(after));
  }, [value, after]);
  return (
    <select className="sched-select" value={value || ''} disabled={!!disabled} aria-label={label}
      onChange={(e) => onChange(e.target.value)}>
      {!value ? <option value="">—</option> : null}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// One implementation of "continue after the last range" for days and overrides.
function appendRange(ranges) {
  return addRange({ weeklyHours: { mon: ranges } }, 'mon').weeklyHours.mon;
}

function RangeEditor({ ranges, onChange, disabled, errors, labelPrefix }) {
  const list = Array.isArray(ranges) ? ranges : [];
  const errs = Array.isArray(errors) ? errors : [];
  const rowErrors = (i) => errs.filter((e) => e.index === i).map((e) => e.message);
  const groupErrors = errs.filter((e) => e.index === -1).map((e) => e.message);
  return (
    <div className="sched-ranges">
      {list.map((r, i) => (
        <div key={i}>
          <div className="sched-range">
            <TimeSelect value={r.start} label={`${labelPrefix} start time ${i + 1}`} disabled={disabled}
              onChange={(v) => onChange(list.map((x, j) => (j === i ? { ...x, start: v } : x)))} />
            <span className="sched-range-dash" aria-hidden="true">–</span>
            <TimeSelect value={r.end} after={r.start} label={`${labelPrefix} end time ${i + 1}`} disabled={disabled}
              onChange={(v) => onChange(list.map((x, j) => (j === i ? { ...x, end: v } : x)))} />
            <button type="button" className="sched-range-x" aria-label={`Remove ${labelPrefix.toLowerCase()} hours ${i + 1}`}
              disabled={disabled} onClick={() => onChange(list.filter((_, j) => j !== i))}>×</button>
          </div>
          {rowErrors(i).length > 0 ? <div className="sched-error" role="alert">{rowErrors(i).join(' · ')}</div> : null}
        </div>
      ))}
      {groupErrors.length > 0 ? <div className="sched-error" role="alert">{groupErrors.join(' · ')}</div> : null}
    </div>
  );
}

function rangesSummary(ranges) {
  return (ranges || []).map((r) => `${formatHhmm12(r.start)} – ${formatHhmm12(r.end)}`).join(', ');
}

/* ── weekly hours ──────────────────────────────────────────────────────── */

function WeeklyHoursCard({ draft, update, canEdit }) {
  return (
    <section className="card sched-card" aria-labelledby="sched-hours-title">
      <div className="sched-card-head">
        <div>
          <h2 id="sched-hours-title">Weekly hours</h2>
          <p>When patients can book. Up to {LIMITS.windowsPerDay} time ranges per day; a break is simply the gap between two.</p>
        </div>
      </div>
      <div className="sched-days">
        {DAYS.map((day) => {
          const ranges = draft.weeklyHours[day] || [];
          const open = ranges.length > 0;
          const errors = validateRanges(ranges);
          return (
            <div className="sched-day" key={day}>
              <div className="sched-day-name">{DAY_LABEL[day]}</div>
              <div className="sched-day-body">
                <div className="sched-day-top">
                  <Toggle checked={open} disabled={!canEdit} text={open ? 'Open' : 'Closed'}
                    label={`${DAY_LABEL[day]}: open for patient scheduling`}
                    onChange={(v) => update((d) => setDayClosed(d, day, !v))} />
                  {open ? (
                    <div className="sched-range-actions">
                      <button type="button" className="btn sm ghost" disabled={!canEdit || ranges.length >= LIMITS.windowsPerDay || hhmmToMin(ranges[ranges.length - 1].end) >= 21 * 60}
                        onClick={() => update((d) => addRange(d, day))}>+ Add hours</button>
                      {WEEKDAYS.includes(day) ? (
                        <button type="button" className="btn sm ghost" disabled={!canEdit}
                          title="Give Monday–Friday these hours"
                          onClick={() => update((d) => copyToWeekdays(d, day))}>Copy to weekdays</button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {open ? (
                  <RangeEditor ranges={ranges} errors={errors} disabled={!canEdit} labelPrefix={DAY_LABEL[day]}
                    onChange={(next) => update((d) => ({ ...d, weeklyHours: { ...d.weeklyHours, [day]: next } }))} />
                ) : (
                  <div className="sched-day-closed">No appointments</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── visit types ───────────────────────────────────────────────────────── */

const MODE_OPTIONS = [{ value: 'in_person', label: 'In person' }, { value: 'video', label: 'Video' }];

function VisitTypeEditor({ vt, onChange, onDone, onRemove, canEdit, isSaved }) {
  const errors = validateVisitType(vt);
  const [touched, setTouched] = useState(false);
  const show = (k) => (touched || (vt.name && k !== 'name')) && errors[k];
  return (
    <div className="sched-vt sched-vt-edit" role="group" aria-label={`Edit visit type ${vt.name || ''}`.trim()}>
      <div className="sched-fields sched-fields-2">
        <div className="sched-field">
          <label className="sched-field-label" htmlFor={`vt-name-${vt.id}`}>Name</label>
          <input id={`vt-name-${vt.id}`} className="sched-input" type="text" value={vt.name} maxLength={LIMITS.name.max}
            placeholder="e.g. New Patient Visit" disabled={!canEdit} autoFocus={!vt.name}
            onChange={(e) => onChange({ ...vt, name: e.target.value })} onBlur={() => setTouched(true)} />
          {show('name') ? <div className="sched-error" role="alert">{errors.name}</div> : null}
        </div>
        <div className="sched-field">
          <label className="sched-field-label" htmlFor={`vt-dur-${vt.id}`}>Length (minutes)</label>
          <input id={`vt-dur-${vt.id}`} className="sched-input" type="number" inputMode="numeric"
            min={LIMITS.durationMin.min} max={LIMITS.durationMin.max} step={LIMITS.durationMin.step}
            value={vt.durationMin} disabled={!canEdit}
            onChange={(e) => onChange({ ...vt, durationMin: e.target.value === '' ? '' : Number(e.target.value) })} onBlur={() => setTouched(true)} />
          {show('durationMin') ? <div className="sched-error" role="alert">{errors.durationMin}</div> : null}
        </div>
      </div>
      <div className="sched-field">
        <span className="sched-field-label">Visit</span>
        <Segmented value={vt.mode} options={MODE_OPTIONS} disabled={!canEdit} label="Visit mode"
          onChange={(mode) => onChange({ ...vt, mode })} />
      </div>
      <div className="sched-field">
        <label className="sched-field-label" htmlFor={`vt-desc-${vt.id}`}>Description patients see <span className="sched-muted" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>(optional)</span></label>
        <textarea id={`vt-desc-${vt.id}`} className="sched-textarea" value={vt.description} maxLength={LIMITS.description.max}
          placeholder="e.g. Your first visit with the doctor. Bring your medication list." disabled={!canEdit}
          onChange={(e) => onChange({ ...vt, description: e.target.value })} />
        <div className="sched-counter">{(vt.description || '').length}/{LIMITS.description.max}</div>
        {errors.description ? <div className="sched-error" role="alert">{errors.description}</div> : null}
      </div>
      <div className="sched-vt-edit-foot">
        <button type="button" className="btn sm danger" disabled={!canEdit} onClick={onRemove}>{isSaved ? 'Remove' : 'Discard'}</button>
        <button type="button" className="btn sm primary" disabled={!canEdit || Object.keys(errors).length > 0}
          onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

function VisitTypesCard({ draft, saved, update, canEdit, apptTypes }) {
  const [editingId, setEditingId] = useState(null);
  const list = draft.visitTypes || [];
  const savedIds = new Set((saved.visitTypes || []).map((v) => v.id));
  const enabledCount = list.filter((v) => v.enabled).length;

  function add(seed) {
    const vt = newVisitType(seed);
    update((d) => upsertVisitType(d, vt));
    setEditingId(vt.id);
  }

  async function remove(vt) {
    if (savedIds.has(vt.id)) {
      const ok = await confirmDialog({
        title: `Remove "${vt.name || 'this visit type'}"?`,
        body: 'Patients will no longer be offered it once you save. Appointments already booked keep their details.',
        confirmLabel: 'Remove', danger: true,
      });
      if (!ok) return;
    }
    update((d) => removeVisitType(d, vt.id));
    if (editingId === vt.id) setEditingId(null);
  }

  // GET /appointment-types passes Elation rows through as {id, name,
  // defaultDuration} without filtering deleted ones (a follow-up adds
  // deleted_date): a row without a name is unusable, and two rows with one
  // name are one option.
  const prefillOptions = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(apptTypes) ? apptTypes : []).filter((t) => {
      if (!t || typeof t.name !== 'string' || !t.name.trim()) return false;
      const key = t.name.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [apptTypes]);

  return (
    <section className="card sched-card" aria-labelledby="sched-vt-title">
      <div className="sched-card-head">
        <div>
          <h2 id="sched-vt-title">Visit types</h2>
          <p>What a patient chooses first. Each one sets the appointment length and whether it is in person or video.</p>
        </div>
        {canEdit && apptTypes !== 'error' ? (
          <div className="sched-card-actions">
            <select className="sched-select" aria-label="Prefill from an Elation appointment type" value=""
              disabled={list.length >= LIMITS.visitTypes.max || apptTypes === null || apptTypes === 'loading'}
              onChange={(e) => {
                const t = prefillOptions.find((x) => String(x.id) === e.target.value);
                if (!t) return;
                const dur = Number(t.defaultDuration);
                const rounded = Number.isFinite(dur) && dur > 0 ? Math.min(LIMITS.durationMin.max, Math.max(LIMITS.durationMin.min, Math.round(dur / 5) * 5)) : 30;
                add({ name: String(t.name || '').slice(0, LIMITS.name.max), durationMin: rounded });
              }}>
              <option value="">{apptTypes === null || apptTypes === 'loading' ? 'Loading Elation types…' : prefillOptions.length ? 'Prefill from Elation…' : 'No Elation appointment types'}</option>
              {prefillOptions.map((t) => (
                <option key={t.id} value={String(t.id)}>{t.name}{t.defaultDuration ? ` · ${t.defaultDuration} min` : ''}</option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {list.length === 0 ? (
        <p className="sched-muted" style={{ margin: '0 0 12px' }}>No visit types yet — patients need at least one to book.</p>
      ) : null}

      <div className="sched-vts">
        {list.map((vt) => (
          editingId === vt.id ? (
            <VisitTypeEditor key={vt.id} vt={vt} canEdit={canEdit} isSaved={savedIds.has(vt.id)}
              onChange={(next) => update((d) => upsertVisitType(d, next))}
              onDone={() => setEditingId(null)}
              onRemove={() => remove(vt)} />
          ) : (
            <div key={vt.id} className={`sched-vt${vt.enabled ? '' : ' is-disabled'}`}>
              <div className="sched-vt-top">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="sched-vt-name">{vt.name || 'Untitled visit type'}</div>
                  <div className="sched-vt-meta">
                    <span className="pill">{vt.durationMin} min</span>
                    <span className={`pill${vt.mode === 'video' ? ' accent' : ''}`}>{modeLabel(vt.mode)}</span>
                    {!vt.enabled ? <span className="pill">Not offered</span> : null}
                  </div>
                </div>
                <div className="sched-vt-actions">
                  <Toggle checked={vt.enabled} disabled={!canEdit} text={vt.enabled ? 'On' : 'Off'}
                    label={`${vt.name || 'Visit type'}: offered to patients`}
                    onChange={(enabled) => update((d) => upsertVisitType(d, { ...vt, enabled }))} />
                  <button type="button" className="btn sm" disabled={!canEdit} onClick={() => setEditingId(vt.id)}>Edit</button>
                </div>
              </div>
              {vt.description ? <div className="sched-vt-desc">{vt.description}</div> : null}
              {Object.keys(validateVisitType(vt)).length > 0 ? (
                <div className="sched-error" role="alert">{Object.values(validateVisitType(vt)).join(' · ')}</div>
              ) : null}
            </div>
          )
        ))}
        {canEdit ? (
          <button type="button" className="sched-add" disabled={list.length >= LIMITS.visitTypes.max} onClick={() => add()}>
            + Add visit type
          </button>
        ) : null}
      </div>

      {list.length > 0 && enabledCount === 0 ? (
        <div className="sched-banner sched-banner-warn" style={{ marginTop: 12, marginBottom: 0 }}>
          Every visit type is switched off, so patients have nothing to book.
        </div>
      ) : null}

      <p className="sched-fine">
        <strong>Names reach Elation.</strong> The first time a visit type is booked, Elation creates an appointment type
        with that exact name. Keep names stable — renaming one later creates a new type in Elation.
      </p>
    </section>
  );
}

/* ── rules ─────────────────────────────────────────────────────────────── */

function RuleSelect({ id, label, help, value, options, onChange, disabled, nullable }) {
  const toKey = (v) => (v == null ? '' : String(v));
  return (
    <div className="sched-field">
      <label className="sched-field-label" htmlFor={id}>{label}</label>
      <select id={id} className="sched-select" value={toKey(value)} disabled={disabled}
        onChange={(e) => onChange(e.target.value === '' && nullable ? null : Number(e.target.value))}>
        {options.map((o) => <option key={toKey(o.value)} value={toKey(o.value)}>{o.label}</option>)}
      </select>
      {help ? <div className="sched-field-help">{help}</div> : null}
    </div>
  );
}

function RulesCard({ draft, update, canEdit }) {
  const r = draft.rules;
  const setRule = (k, v) => update((d) => ({ ...d, rules: { ...d.rules, [k]: v } }));
  const hours = (v) => (v % 24 === 0 && v >= 24 ? `${v / 24} day${v === 24 ? '' : 's'}` : `${v} hours`);
  return (
    <section className="card sched-card" aria-labelledby="sched-rules-title">
      <div className="sched-card-head">
        <div>
          <h2 id="sched-rules-title">Rules</h2>
          <p>How close to now, how far ahead, and how the day is divided.</p>
        </div>
      </div>
      <div className="sched-fields">
        <RuleSelect id="rule-notice" label="Minimum notice" value={r.minNoticeHours} disabled={!canEdit}
          options={withCurrentOption(MIN_NOTICE_OPTIONS, r.minNoticeHours, hours)}
          help="The earliest a patient can book, counted from now."
          onChange={(v) => setRule('minNoticeHours', v)} />
        <RuleSelect id="rule-advance" label="Book up to" value={r.maxAdvanceDays} disabled={!canEdit}
          options={withCurrentOption(MAX_ADVANCE_OPTIONS, r.maxAdvanceDays, (v) => `${v} days`)}
          help="How far ahead the calendar opens."
          onChange={(v) => setRule('maxAdvanceDays', v)} />
        <RuleSelect id="rule-spacing" label="Slot spacing" value={r.slotGranularityMin} disabled={!canEdit} nullable
          options={withCurrentOption(GRANULARITY_OPTIONS, r.slotGranularityMin, (v) => `Every ${v} minutes`)}
          help="Match visit length keeps a 30-minute visit on a 30-minute grid; a shorter spacing offers more start times."
          onChange={(v) => setRule('slotGranularityMin', v)} />
        <RuleSelect id="rule-buffer" label="Buffer between visits" value={r.bufferMin} disabled={!canEdit}
          options={withCurrentOption(BUFFER_OPTIONS, r.bufferMin, (v) => `${v} minutes`)}
          help="Kept free before and after each booked visit."
          onChange={(v) => setRule('bufferMin', v)} />
        <RuleSelect id="rule-hold" label="Hold a picked time for" value={r.holdTtlMin} disabled={!canEdit}
          options={withCurrentOption(HOLD_OPTIONS, r.holdTtlMin, (v) => `${v} minutes`)}
          help="While the patient finishes the form, their pick is reserved; they see this as a countdown."
          onChange={(v) => setRule('holdTtlMin', v)} />
      </div>
    </section>
  );
}

/* ── calendar (physician, timezone, checklist, status) ─────────────────── */

function CalendarCard({ draft, update, canEdit, isAdmin, server, physicians, onRetryPhysicians, onOpenSettings, dirty, timezone }) {
  const checklist = setupChecklist(draft);
  const list = Array.isArray(physicians) ? physicians : [];
  const current = draft.physicianId;
  const options = current && !list.some((p) => Number(p.id) === current)
    ? list.concat([{ id: current, name: `Physician #${current}` }])
    : list;
  const items = [
    { key: 'physician', done: checklist.physician, label: 'Calendar physician chosen' },
    { key: 'hours', done: checklist.hours, label: 'Hours on at least one day' },
    { key: 'visitType', done: checklist.visitType, label: 'At least one visit type offered' },
  ];
  const on = server.enabled === true;
  let statusText;
  if (on && server.bookable) statusText = 'Patients see the appointment step at the end of the intake link.';
  else if (on && checklist.complete && dirty) statusText = 'Turned on. Save your changes to finish setup — patients will then see the step.';
  else if (on) statusText = 'Turned on, but patients won’t see the step until every item above is done and saved.';
  else if (isAdmin) statusText = 'Off. Once setup is complete, turn it on in Settings → Patient scheduling.';
  else statusText = 'Off. An admin can turn it on in Settings → Patient scheduling once setup is complete.';

  return (
    <section className="card sched-card" aria-labelledby="sched-cal-title">
      <div className="sched-card-head">
        <div>
          <h2 id="sched-cal-title">Calendar</h2>
          <p>One practice-wide calendar, booked onto one Elation physician. Elation’s own appointments count as busy.</p>
        </div>
      </div>

      {server.intakeLinksEnabled === false ? (
        <div className="sched-banner sched-banner-warn" role="status">
          Intake links are turned off for this practice, so patients can’t reach the appointment step and held times
          won’t be booked into Elation until links are back on.
        </div>
      ) : null}

      <div className="sched-kv">
        <div className="sched-kv-row">
          <label className="sched-kv-label" htmlFor="sched-physician">Calendar physician</label>
          <select id="sched-physician" className="sched-select" value={current || ''} disabled={!canEdit || physicians === null}
            onChange={(e) => update((d) => ({ ...d, physicianId: normalizePhysicianId(e.target.value) }))}>
            <option value="">{physicians === null ? 'Loading physicians…' : '— Choose a physician —'}</option>
            {options.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
          {physicians === 'error' ? (
            <div className="sched-error" role="alert">
              Couldn’t load the physician list from Elation.{' '}
              <button type="button" className="btn sm ghost" onClick={onRetryPhysicians}>Try again</button>
            </div>
          ) : null}
        </div>
        <div className="sched-kv-row">
          <span className="sched-kv-label">Timezone</span>
          <div className="sched-kv-value" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>{tzLabel(timezone)}</span>
            {isAdmin ? (
              <button type="button" className="btn sm ghost" onClick={onOpenSettings}>Change in Settings</button>
            ) : (
              <span className="sched-muted">Set by an admin in Settings</span>
            )}
          </div>
        </div>
        <div className="sched-kv-row">
          <span className="sched-kv-label">Service location</span>
          <div className="sched-kv-value">
            {server.serviceLocationId != null
              ? `Elation location #${server.serviceLocationId}`
              : <span className="sched-muted">Not found in Elation — appointments are booked without one</span>}
          </div>
        </div>
        <div className="sched-kv-row">
          <span className="sched-kv-label">Setup</span>
          <ul className="sched-checklist">
            {items.map((it) => (
              <li key={it.key} className={`sched-check${it.done ? ' is-done' : ''}`}>
                <span className="sched-check-dot" aria-hidden="true">{it.done ? '✓' : '·'}</span>
                <span>{it.label}{it.done ? '' : ' — not yet'}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="sched-status">
        <span className={`pill${on ? ' ok' : ''}`}>{on ? 'ON' : 'OFF'}</span>
        <span className="sched-status-text">{statusText}</span>
        <button type="button" className="btn sm" onClick={onOpenSettings}>Open Settings</button>
      </div>
    </section>
  );
}

/* ── date overrides ────────────────────────────────────────────────────── */

const OVERRIDE_KIND = [{ value: 'closed', label: 'Closed' }, { value: 'custom', label: 'Custom hours' }];

function formatOverrideDate(date, today) {
  if (!parseYmd(date)) return date || '—';
  const p = dayParts(date);
  const ty = parseYmd(today).y;
  return p.year === ty ? formatDayMedium(date) : `${formatDayMedium(date)}, ${p.year}`;
}

function OverrideForm({ initial, today, canEdit, onCancel, onSubmit, existingDates }) {
  const [form, setForm] = useState(() => ({
    date: initial ? initial.date : '',
    kind: initial && initial.closed === false ? 'custom' : 'closed',
    windows: initial && Array.isArray(initial.windows) && initial.windows.length ? initial.windows : [{ start: '09:00', end: '12:00' }],
    note: (initial && initial.note) || '',
  }));
  const candidate = form.kind === 'closed'
    ? { date: form.date, closed: true, note: form.note.trim() }
    : { date: form.date, closed: false, windows: form.windows, note: form.note.trim() };
  const errors = validateOverride(candidate, { todayYmd: today });
  const replaces = !initial && existingDates.has(form.date);
  // The form's own lower bound (the validator has none — the server keeps past
  // rows): a date already gone cannot be ADDED, only kept.
  const pastPick = !initial && !!form.date && !!parseYmd(form.date) && compareYmd(form.date, today) < 0;
  const dateError = errors.date || (pastPick ? 'That date has passed' : null);
  const maxDate = addDays(today, LIMITS.overrides.futureDays);
  return (
    <div className="sched-ov-form" role="group" aria-label={initial ? 'Edit date override' : 'Add date override'}>
      <div className="sched-fields sched-fields-2">
        <div className="sched-field">
          <label className="sched-field-label" htmlFor="ov-date">Date</label>
          <input id="ov-date" className="sched-input" type="date" value={form.date} min={today} max={maxDate} disabled={!canEdit || !!initial}
            onChange={(e) => setForm({ ...form, date: e.target.value })} />
          {form.date && dateError ? <div className="sched-error" role="alert">{dateError}</div> : null}
          {replaces ? <div className="sched-field-help">Replaces the existing override on this date.</div> : null}
        </div>
        <div className="sched-field">
          <span className="sched-field-label">That day is</span>
          <Segmented value={form.kind} options={OVERRIDE_KIND} disabled={!canEdit} label="Override kind"
            onChange={(kind) => setForm({ ...form, kind })} />
        </div>
      </div>
      {form.kind === 'custom' ? (
        <div className="sched-field">
          <span className="sched-field-label">Hours that day</span>
          <RangeEditor ranges={form.windows} errors={errors.windows || []} disabled={!canEdit} labelPrefix="Override"
            onChange={(windows) => setForm({ ...form, windows })} />
          <div>
            <button type="button" className="btn sm ghost"
              disabled={!canEdit || form.windows.length >= LIMITS.windowsPerDay}
              onClick={() => setForm({ ...form, windows: appendRange(form.windows) })}>+ Add hours</button>
          </div>
        </div>
      ) : null}
      <div className="sched-field">
        <label className="sched-field-label" htmlFor="ov-note">Note <span className="sched-muted" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>(optional, for your team)</span></label>
        <input id="ov-note" className="sched-input" type="text" value={form.note} maxLength={LIMITS.overrides.noteMax} placeholder="e.g. Thanksgiving"
          disabled={!canEdit} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        {errors.note ? <div className="sched-error" role="alert">{errors.note}</div> : null}
      </div>
      <div className="sched-ov-form-foot">
        <button type="button" className="btn sm ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn sm primary" disabled={!canEdit || !form.date || pastPick || Object.keys(errors).length > 0}
          onClick={() => onSubmit(candidate)}>{initial ? 'Save override' : 'Add override'}</button>
      </div>
    </div>
  );
}

function OverridesCard({ draft, update, canEdit, today }) {
  const [form, setForm] = useState(null); // null | 'new' | override (editing)
  const [showPast, setShowPast] = useState(false);
  const { upcoming, past } = splitPastOverrides(draft.overrides || [], today);
  const existingDates = new Set((draft.overrides || []).map((o) => o.date));

  function submit(ov) {
    update((d) => upsertOverride(d, ov));
    setForm(null);
  }

  const row = (ov, isPast) => {
    const errs = validateOverride(ov, { todayYmd: today });
    return (
      <div key={ov.date} className={`sched-ov${isPast ? ' is-past' : ''}`}>
        <div className="sched-ov-date">{formatOverrideDate(ov.date, today)}</div>
        <div className="sched-ov-main">
          <div className="sched-ov-hours">
            {ov.closed !== false ? <span className="pill">Closed</span> : rangesSummary(ov.windows) || <span className="pill warn">No hours</span>}
          </div>
          {ov.note ? <div className="sched-ov-note">{ov.note}</div> : null}
          {!isPast && Object.keys(errs).length > 0 ? (
            <div className="sched-error" role="alert">
              {[errs.date, ...(errs.windows || []).map((w) => w.message)].filter(Boolean).join(' · ')}
            </div>
          ) : null}
        </div>
        {canEdit ? (
          <div className="sched-ov-actions">
            {!isPast ? <button type="button" className="btn sm" onClick={() => setForm(ov)}>Edit</button> : null}
            <button type="button" className="btn sm ghost" aria-label={`Remove override on ${formatOverrideDate(ov.date, today)}`}
              onClick={() => update((d) => removeOverride(d, ov.date))}>Remove</button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <section className="card sched-card" aria-labelledby="sched-ov-title">
      <div className="sched-card-head">
        <div>
          <h2 id="sched-ov-title">Date overrides</h2>
          <p>Holidays and one-off days. An override replaces that day’s weekly hours.</p>
        </div>
        {canEdit && form === null ? (
          <div className="sched-card-actions">
            <button type="button" className="btn sm" disabled={(draft.overrides || []).length >= LIMITS.overrides.max}
              onClick={() => setForm('new')}>+ Add a date</button>
          </div>
        ) : null}
      </div>

      {form !== null ? (
        <div style={{ marginBottom: 12 }}>
          <OverrideForm initial={form === 'new' ? null : form} today={today} canEdit={canEdit}
            existingDates={existingDates} onCancel={() => setForm(null)} onSubmit={submit} />
        </div>
      ) : null}

      {upcoming.length === 0 && form === null ? (
        <p className="sched-muted" style={{ margin: 0 }}>No upcoming overrides. The weekly hours apply every week.</p>
      ) : null}

      <div className="sched-ovs">
        {upcoming.map((ov) => row(ov, false))}
      </div>

      {past.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <button type="button" className="btn sm ghost" aria-expanded={showPast} onClick={() => setShowPast((v) => !v)}>
            {showPast ? 'Hide past' : `Show past (${past.length})`}
          </button>
          {showPast ? <div className="sched-ovs" style={{ marginTop: 8 }}>{past.map((ov) => row(ov, true))}</div> : null}
        </div>
      ) : null}
    </section>
  );
}

/* ── next 7 days preview ───────────────────────────────────────────────── */

function PreviewCard({ preview, draft, today, timezone }) {
  const enabledTypes = (draft.visitTypes || []).filter((v) => v.enabled);
  const data = preview.data;
  const days = data && Array.isArray(data.days) ? data.days : null;
  const byDate = new Map((days || []).map((d) => [d.date, d]));
  const window = weekWindow(today, 7).map((ymd) => byDate.get(ymd) || { date: ymd, counts: {} });
  const { rows } = previewBars(window, enabledTypes);
  const colorFor = (idx) => `sched-seg-${Math.min(idx, 5)}`;
  const typeIndex = new Map(enabledTypes.map((t, i) => [t.id, i]));

  let stateLine = null;
  if (preview.status === 'invalid') stateLine = 'Fix the highlighted problems to preview.';
  else if (preview.status === 'loading') stateLine = 'Updating…';
  else if (preview.status === 'error') stateLine = 'Preview unavailable right now — saving still works.';
  else if (preview.status === 'ready' && data && data.unavailable) stateLine = 'Elation’s calendar couldn’t be read, so these counts don’t subtract busy time.';

  // The last good rows stay on screen while the draft is invalid, loading or
  // failed (dimmed) — a blank card would read as "no openings".
  const showRows = preview.status === 'ready' || (days && (preview.status === 'loading' || preview.status === 'error' || preview.status === 'invalid'));
  const dimRows = preview.status !== 'ready';

  return (
    <section className="card sched-card" aria-labelledby="sched-preview-title">
      <div className="sched-card-head">
        <div>
          <h2 id="sched-preview-title">Next 7 days</h2>
          <p>Openings a patient would see with these settings, in {tzLabel(timezone)}. Counts subtract Elation busy time.</p>
        </div>
      </div>
      <div className="sched-preview" aria-live="polite" aria-busy={preview.status === 'loading'}>
        {showRows ? rows.map((r) => (
          <div className="sched-preview-row" key={r.date} style={dimRows ? { opacity: 0.45 } : undefined}>
            <div className="sched-preview-day">{formatDayMedium(r.date)}</div>
            <div className="sched-bar" role="img" aria-label={`${r.total} openings`}>
              {r.segments.filter((s) => s.count > 0).map((s) => (
                <div key={s.id} className={`sched-bar-seg ${colorFor(typeIndex.has(s.id) ? typeIndex.get(s.id) : 5)}`}
                  style={{ width: `${s.pct}%` }} title={`${s.name}: ${s.count}`} />
              ))}
            </div>
            <div className="sched-preview-count">{r.total === 0 ? 'no openings' : `${r.total} ${r.total === 1 ? 'opening' : 'openings'}`}</div>
          </div>
        )) : (
          Array.from({ length: 7 }, (_, i) => <div className="sched-skeleton" key={i} />)
        )}
        {showRows && enabledTypes.length > 1 ? (
          <div className="sched-legend">
            {enabledTypes.map((t, i) => (
              <span className="sched-legend-item" key={t.id}>
                <span className={`sched-legend-swatch ${colorFor(i)}`} aria-hidden="true" />{t.name}
              </span>
            ))}
          </div>
        ) : null}
        <div className="sched-preview-state">{stateLine}</div>
        {data && Array.isArray(data.warnings) && data.warnings.length > 0 ? (
          <div className="sched-banner sched-banner-warn" style={{ marginBottom: 0, opacity: dimRows ? 0.45 : 1 }}>
            <ul style={{ margin: 0 }}>{data.warnings.map((w, i) => <li key={i}>{typeof w === 'string' ? w : (w && w.message) || ''}</li>)}</ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ── the page ──────────────────────────────────────────────────────────── */

export function SchedulingPage({ user, onOpenSettings, settingsOpen }) {
  const role = String((user && user.role) || '').toLowerCase();
  const isAdmin = role === 'admin';
  const canEdit = isAdmin || role === 'provider';

  const [phase, setPhase] = useState('loading'); // loading | ready | forbidden | error
  const [errorMsg, setErrorMsg] = useState('');
  const [server, setServer] = useState(null);  // GET /config body
  const [saved, setSaved] = useState(null);    // normalized saved config
  const [draft, setDraft] = useState(null);    // normalized working copy
  const [restored, setRestored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null); // { message, fields }
  const [conflict, setConflict] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [physicians, setPhysicians] = useState(null); // null | [] | 'error'
  const [apptTypes, setApptTypes] = useState(null);   // null (not loaded) | 'loading' | [] | 'error'
  const [preview, setPreview] = useState({ status: 'idle', data: null });
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const timezone = (server && server.timezone) || DEFAULT_TZ;
  const today = useMemo(() => todayYmd(Date.now(), timezone), [timezone]);

  const load = useCallback(async () => {
    try {
      const data = await ccmApi.getSchedulingConfig();
      if (!mountedRef.current) return;
      const cfg = normalizeConfig(data && data.config);
      setServer(data || {});
      setSaved(cfg);
      if (retainedDraft && retainedDraft.version === cfg.version && configFingerprint(retainedDraft.draft) !== configFingerprint(cfg)) {
        setDraft(retainedDraft.draft);
        setRestored(true);
      } else {
        // The parked edits were made against a version that has since moved on
        // — same loss as a 409, same message.
        if (retainedDraft && retainedDraft.version !== cfg.version) setConflict(true);
        retainedDraft = null;
        setDraft(cfg);
        setRestored(false);
      }
      setPhase('ready');
    } catch (e) {
      if (!mountedRef.current) return;
      if (e && e.status === 403) { setPhase('forbidden'); setErrorMsg((e && e.message) || ''); return; }
      setPhase('error'); setErrorMsg((e && e.message) || 'Could not load scheduling.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The on/off switch is the admin toggle in the Settings overlay. When that
  // overlay closes, re-read enabled/bookable/intakeLinksEnabled so the status
  // here never says OFF right after the admin turned it on. The draft is left
  // alone; only a clean (non-dirty) page adopts a newer saved config.
  const settingsWasOpen = useRef(false);
  useEffect(() => {
    if (settingsWasOpen.current && !settingsOpen && phase === 'ready') {
      ccmApi.getSchedulingConfig().then((data) => {
        if (!mountedRef.current || !data) return;
        setServer(data);
        const cfg = normalizeConfig(data.config);
        if (!latest.current || !latest.current.dirty) { setSaved(cfg); setDraft(cfg); }
      }).catch(() => { /* the next load or save refreshes it */ });
    }
    settingsWasOpen.current = !!settingsOpen;
  }, [settingsOpen, phase]);

  const loadPhysicians = useCallback(() => {
    setPhysicians(null);
    ccmApi.listSchedulingPhysicians()
      .then((list) => { if (mountedRef.current) setPhysicians(Array.isArray(list) ? list : (list && Array.isArray(list.physicians) ? list.physicians : [])); })
      .catch(() => { if (mountedRef.current) setPhysicians('error'); });
  }, []);
  useEffect(() => { if (phase === 'ready') loadPhysicians(); }, [phase, loadPhysicians]);

  const loadApptTypes = useCallback(() => {
    if (apptTypes !== null) return;
    setApptTypes('loading');
    ccmApi.listSchedulingAppointmentTypes()
      .then((list) => { if (mountedRef.current) setApptTypes(Array.isArray(list) ? list : (list && Array.isArray(list.appointmentTypes) ? list.appointmentTypes : [])); })
      .catch(() => { if (mountedRef.current) setApptTypes('error'); });
  }, [apptTypes]);
  // Loaded with the page rather than on focus: a native picker opened at focus
  // would show one option until closed and reopened.
  useEffect(() => { if (phase === 'ready' && canEdit) loadApptTypes(); }, [phase, canEdit, loadApptTypes]);

  const draftKey = draft ? configFingerprint(draft) : '';
  const savedKey = saved ? configFingerprint(saved) : '';
  const dirty = !!draft && !!saved && draftKey !== savedKey;
  // The document that would be SENT: normalized. Validation, the preview and
  // Save all read this one, so what is judged is exactly what goes over the
  // wire. Past overrides are sent as they are — the server accepts them (plan
  // §12 addendum). Keyed on the fingerprint AND the version: after a save the
  // content may be unchanged while the version moved on, and the next Save
  // must carry the new one.
  const sendable = useMemo(() => (draft ? normalizeConfig(draft) : null), [draftKey, draft ? draft.version : -1]); // eslint-disable-line react-hooks/exhaustive-deps
  const validation = useMemo(() => (sendable ? validateConfig(sendable, { todayYmd: today }) : { ok: false, errors: [] }), [sendable, today]);

  // Park the draft for the next mount (tab switches unmount pages).
  const latest = useRef(null);
  latest.current = { draft, saved, dirty };
  useEffect(() => () => {
    const cur = latest.current;
    if (cur && cur.dirty && cur.draft && cur.saved) retainedDraft = { version: cur.saved.version, draft: cur.draft };
    else retainedDraft = null;
  }, []);

  // Leaving the site with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Live preview of the DRAFT: debounced 500 ms, sequence-guarded so a slow
  // older answer can never overwrite a newer one. Only a valid draft is sent.
  const previewSeq = useRef(0);
  useEffect(() => {
    if (phase !== 'ready' || !draft) return undefined;
    // An answer still in flight for the last VALID draft must not un-dim the
    // card once the draft has turned invalid: retire it.
    if (!validation.ok) { previewSeq.current += 1; setPreview((p) => ({ status: 'invalid', data: p.data })); return undefined; }
    const seq = ++previewSeq.current;
    setPreview((p) => ({ status: 'loading', data: p.data }));
    const t = setTimeout(() => {
      ccmApi.previewSchedulingSlots({ config: sendable })
        .then((data) => { if (mountedRef.current && seq === previewSeq.current) setPreview({ status: 'ready', data }); })
        .catch(() => { if (mountedRef.current && seq === previewSeq.current) setPreview((p) => ({ status: 'error', data: p.data })); });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draftKey, validation.ok, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback((fn) => {
    if (!canEdit) return;
    setDraft((d) => (d ? fn(d) : d));
    setSaveError(null);
    setConflict(false);
  }, [canEdit]);

  async function save() {
    if (!canEdit || saving || !sendable || !validation.ok) return;
    setSaving(true); setSaveError(null); setConflict(false);
    try {
      const sent = sendable;
      const data = await ccmApi.saveSchedulingConfig(sent);
      if (!mountedRef.current) return;
      const cfg = normalizeConfig(data && data.config);
      retainedDraft = null;
      setServer(data || {});
      setSaved(cfg);
      // Edits made while the request was in flight are kept (see the header).
      setDraft((cur) => reconcileAfterSave(cur, sent, cfg));
      setRestored(false);
      setSavedFlash(true);
      setTimeout(() => { if (mountedRef.current) setSavedFlash(false); }, 3000);
    } catch (e) {
      if (!mountedRef.current) return;
      if (e && e.status === 403) { setPhase('forbidden'); setErrorMsg((e && e.message) || ''); return; }
      if (e && e.status === 409 && e.body && e.body.code === 'version_conflict') {
        // Someone else saved these settings first. Their version wins; the
        // person's edits are dropped and they are told so — a silent merge
        // would overwrite what the other person just did.
        retainedDraft = null;
        await load();
        if (mountedRef.current) setConflict(true);
        return;
      }
      const fields = e && e.body && Array.isArray(e.body.fields) ? e.body.fields : [];
      setSaveError({ message: (e && e.message) || 'Could not save.', fields });
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }

  async function discard() {
    const ok = await confirmDialog({
      title: 'Discard unsaved changes?',
      body: 'Your edits to hours, visit types, date overrides and rules will be lost.',
      confirmLabel: 'Discard changes',
      danger: true,
    });
    if (!ok) return;
    retainedDraft = null;
    setDraft(saved);
    setRestored(false);
    setSaveError(null);
  }

  /* ── render ─────────────────────────────────────────────────────────── */

  if (phase === 'loading') {
    return (
      <div className="sched-page" aria-busy="true">
        <div className="sched-head"><div><h1>Scheduling</h1><p className="sched-sub">Loading…</p></div></div>
        <div className="sched-grid">
          <div className="card sched-card"><div className="sched-skeleton" /><div className="sched-skeleton" /><div className="sched-skeleton" /></div>
          <div className="card sched-card"><div className="sched-skeleton" /><div className="sched-skeleton" /></div>
        </div>
      </div>
    );
  }
  if (phase === 'forbidden') {
    return (
      <div className="sched-page">
        <div className="sched-head"><div><h1>Scheduling</h1></div></div>
        <div className="card sched-card sched-muted">{errorMsg || 'You do not have access to scheduling. Ask an admin to grant the Scheduling feature to your role.'}</div>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div className="sched-page">
        <div className="sched-head"><div><h1>Scheduling</h1></div></div>
        <div className="sched-banner sched-banner-error" role="alert">
          <span>⚠ {errorMsg}</span>
          <button type="button" className="btn sm" onClick={() => { setPhase('loading'); load(); }}>Retry</button>
        </div>
      </div>
    );
  }

  const errorLines = validation.errors.map((e) => e.message);
  const serverFieldLines = saveError && saveError.fields.length
    ? saveError.fields.map((f) => (f && f.message ? `${f.path ? `${f.path}: ` : ''}${f.message}` : '')).filter(Boolean)
    : [];

  return (
    <div className="sched-page">
      <div className="sched-head">
        <div>
          <h1>Scheduling</h1>
          <p className="sched-sub">
            Set the hours, visit types and rules patients use to pick a time on the last step of the intake link.
            Appointments book onto the practice calendar in Elation.
          </p>
        </div>
        <div className="sched-head-right">
          {savedFlash ? <span className="pill ok" role="status">Saved</span> : null}
          {dirty ? <span className="pill warn">Unsaved changes</span> : null}
          <span className={`pill${server.enabled ? ' ok' : ''}`}>Self-scheduling {server.enabled ? 'ON' : 'OFF'}</span>
        </div>
      </div>

      {!canEdit ? (
        <div className="sched-banner sched-banner-info sched-readonly" role="status">
          Read-only — admins and providers can change these settings.
        </div>
      ) : null}
      {restored ? (
        <div className="sched-banner sched-banner-info" role="status">
          <span>Your unsaved changes were kept.</span>
          <button type="button" className="btn sm ghost" onClick={() => setRestored(false)} aria-label="Dismiss">Dismiss</button>
        </div>
      ) : null}
      {conflict ? (
        <div className="sched-banner sched-banner-warn" role="alert">
          <span>Someone else saved these settings first — reloaded with their version. Your unsaved edits were not applied.</span>
          <button type="button" className="btn sm ghost" onClick={() => setConflict(false)} aria-label="Dismiss">Dismiss</button>
        </div>
      ) : null}
      {saveError ? (
        <div className="sched-banner sched-banner-error" role="alert">
          <div>
            <div>Couldn’t save: {saveError.message}</div>
            {serverFieldLines.length ? <ul>{serverFieldLines.map((l, i) => <li key={i}>{l}</li>)}</ul> : null}
          </div>
        </div>
      ) : null}

      <div className="sched-grid">
        <div className="sched-col">
          <WeeklyHoursCard draft={draft} update={update} canEdit={canEdit} />
        </div>
        <div className="sched-col">
          <CalendarCard draft={draft} update={update} canEdit={canEdit} isAdmin={isAdmin} server={server}
            physicians={physicians} onRetryPhysicians={loadPhysicians} onOpenSettings={onOpenSettings}
            dirty={dirty} timezone={timezone} />
          <VisitTypesCard draft={draft} saved={saved} update={update} canEdit={canEdit}
            apptTypes={apptTypes} />
          <RulesCard draft={draft} update={update} canEdit={canEdit} />
        </div>
      </div>
      <div className="sched-row2">
        <OverridesCard draft={draft} update={update} canEdit={canEdit} today={today} />
        <PreviewCard preview={preview} draft={draft} today={today} timezone={timezone} />
      </div>
      <CalendarSyncCard canEdit={canEdit} physicians={physicians} />

      {canEdit && dirty ? (
        <div className="sched-savebar" role="region" aria-label="Unsaved changes">
          <div className="sched-savebar-text">
            Unsaved changes
            {!validation.ok ? (
              <span className="sched-muted">{errorLines.length === 1 ? '1 problem to fix before saving' : `${errorLines.length} problems to fix before saving`}</span>
            ) : (
              <span className="sched-muted">Patients see these settings once saved.</span>
            )}
          </div>
          <div className="sched-savebar-actions">
            <button type="button" className="btn ghost" disabled={saving} onClick={discard}>Discard</button>
            <button type="button" className="btn primary" disabled={saving || !validation.ok} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
          {!validation.ok ? (
            <ul className="sched-savebar-errors" role="alert">
              {errorLines.slice(0, 6).map((l, i) => <li key={i}>{l}</li>)}
              {errorLines.length > 6 ? <li>…and {errorLines.length - 6} more</li> : null}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default SchedulingPage;
